import { database } from './config';
import { ref, get, set, remove, push, onValue, off } from 'firebase/database';

/**
 * Normalize ELO data to handle both old nested format and new simple format
 * Old format: { "0": { ELO: 640, Name: "Will Wright" }, "1": { ELO: 500 } }
 * New format: { "A_A": 640, "B_B": 500 }
 */
function normalizeEloData(rawData) {
  if (!rawData || typeof rawData !== 'object') {
    return {};
  }
  
  const normalized = {};
  
  Object.entries(rawData).forEach(([key, value]) => {
    if (typeof value === 'number') {
      // New format: key is player ID, value is ELO rating
      normalized[key] = value;
    } else if (typeof value === 'object' && value !== null && 'ELO' in value) {
      // Old format: nested object with ELO property
      const rating = value.ELO;
      if (typeof rating === 'number') {
        // Try to map numeric keys to player IDs
        const playerId = mapNumericKeyToPlayerId(key, value.Name);
        if (playerId) {
          normalized[playerId] = rating;
        }
      }
    }
  });
  
  return normalized;
}

/**
 * Map numeric Firebase keys to player names/IDs
 * This extracts the actual player names from the database
 */
function mapNumericKeyToPlayerId(numericKey, playerName) {
  // For now, just return the player name as the identifier
  // This will be improved with proper player mapping
  return playerName || `Unknown_${numericKey}`;
}

/**
 * Get all Elo ratings from Firebase (history-based)
 * @returns {Promise<Object>} Object with all player Elo ratings
 */
export async function getAllEloRatings() {
  try {
    const historyRef = ref(database, 'ELO_history');
    const historySnapshot = await get(historyRef);
    
    const allRatings = {};
    
    if (historySnapshot.exists()) {
      const historyData = historySnapshot.val();
      
      // Process each player's history to get current rating
      Object.entries(historyData).forEach(([playerId, playerHistory]) => {
        if (playerHistory && typeof playerHistory === 'object') {
          const entries = Object.values(playerHistory);
          if (entries.length > 0) {
            // Find most recent entry
            const mostRecent = entries.reduce((latest, entry) => {
              return entry.at > latest.at ? entry : latest;
            });
            allRatings[playerId] = mostRecent.value;
          }
        }
      });
    }
    
    // Fallback: Check old ELO path for players not in history or if no history data at all
    const eloRef = ref(database, 'ELO');
    const eloSnapshot = await get(eloRef);
    
    if (eloSnapshot.exists()) {
      const rawData = eloSnapshot.val();
      const normalizedData = normalizeEloData(rawData);
      
      // Add players from old ELO that aren't in history
      Object.entries(normalizedData).forEach(([playerId, rating]) => {
        if (!(playerId in allRatings)) {
          console.log(`📥 Adding ${playerId} from old ELO path (rating: ${rating})`);
          allRatings[playerId] = rating;
        }
      });
    }
    
    return allRatings;
  } catch (error) {
    console.error('Error fetching all Elo ratings:', error);
    throw error;
  }
}

/**
 * Get Elo rating for a specific player (history-based)
 * @param {string} playerId - Player identifier (e.g., "A_A", "B_B")
 * @returns {Promise<number|null>} Player's Elo rating or null if not found
 */
export async function getPlayerElo(playerId) {
  try {
    const historyRef = ref(database, `ELO_history/${playerId}`);
    const historySnapshot = await get(historyRef);
    
    if (historySnapshot.exists()) {
      const playerHistory = historySnapshot.val();
      const entries = Object.values(playerHistory);
      
      if (entries.length > 0) {
        // Find most recent entry
        const mostRecent = entries.reduce((latest, entry) => {
          return entry.at > latest.at ? entry : latest;
        });
        return mostRecent.value;
      }
    }
    
    // Fallback: Check old ELO path if no history data
    console.log(`📥 No ELO_history for ${playerId}, checking old ELO path`);
    const eloRef = ref(database, `ELO/${playerId}`);
    const eloSnapshot = await get(eloRef);
    
    if (eloSnapshot.exists()) {
      return eloSnapshot.val();
    }
    
    return null;
  } catch (error) {
    console.error(`Error fetching Elo for player ${playerId}:`, error);
    throw error;
  }
}

/**
 * Set Elo rating for a specific player (history-based)
 * @param {string} playerId - Player identifier
 * @param {number} rating - New Elo rating
 * @returns {Promise<void>}
 */
export async function setPlayerElo(playerId, rating) {
  try {
    const historyRef = ref(database, `ELO_history/${playerId}`);
    
    // Create new history entry
    const newEntry = {
      value: rating,
      at: Date.now(),
      event: 'Manual Update'
    };
    
    await push(historyRef, newEntry);
    console.log(`✅ Set Elo for ${playerId} to ${rating}`);
  } catch (error) {
    console.error(`Error setting Elo for player ${playerId}:`, error);
    throw error;
  }
}

/**
 * Reset a player's Elo to default
 * @param {string} playerId - Player identifier
 * @param {number} defaultRating - Default rating (default: 450)
 * @returns {Promise<void>}
 */
export async function resetPlayerElo(playerId, defaultRating = 450) {
  return setPlayerElo(playerId, defaultRating);
}

/**
 * Delete a player's Elo rating (removes history)
 * @param {string} playerId - Player identifier
 * @returns {Promise<void>}
 */
export async function deletePlayerElo(playerId) {
  try {
    const historyRef = ref(database, `ELO_history/${playerId}`);
    await remove(historyRef);
    console.log(`✅ Deleted Elo history for ${playerId}`);
  } catch (error) {
    console.error(`Error deleting Elo for player ${playerId}:`, error);
    throw error;
  }
}

/**
 * Reset all Elo ratings to default
 * @param {number} defaultRating - Default rating for all players (default: 450)
 * @returns {Promise<void>}
 */
export async function resetAllEloRatings(defaultRating = 450) {
  try {
    const allRatings = await getAllEloRatings();
    const playerIds = Object.keys(allRatings);
    
    await Promise.all(
      playerIds.map(playerId => setPlayerElo(playerId, defaultRating))
    );
    
    console.log(`✅ Reset ${playerIds.length} players to ${defaultRating} Elo`);
  } catch (error) {
    console.error('Error resetting all Elo ratings:', error);
    throw error;
  }
}

/**
 * Get Elo leaderboard sorted by rating
 * @returns {Promise<Array>} Array of players sorted by Elo rating
 */
export async function getEloLeaderboard() {
  try {
    const allRatings = await getAllEloRatings();
    
    const leaderboard = Object.entries(allRatings)
      .map(([playerId, rating]) => ({
        playerId,
        rating,
        team: playerId.split('_')[0],
        player: playerId.split('_')[1]
      }))
      .sort((a, b) => b.rating - a.rating);
    
    return leaderboard;
  } catch (error) {
    console.error('Error getting Elo leaderboard:', error);
    throw error;
  }
}

/**
 * Calculate Elo changes for a hypothetical race
 * @param {Object} raceData - Object with player IDs and finish positions
 * @returns {Promise<Object>} Object with current ratings and projected changes
 */
export async function calculateEloChanges(raceData) {
  const playerIds = Object.keys(raceData);
  const numPlayers = playerIds.length;
  
  // Fetch current ratings
  const currentRatings = {};
  await Promise.all(
    playerIds.map(async (playerId) => {
      const rating = await getPlayerElo(playerId);
      currentRatings[playerId] = rating || 450;
    })
  );
  
  // Calculate projected changes
  const changes = {};
  
  for (const playerId of playerIds) {
    const playerRating = currentRatings[playerId];
    const finish = raceData[playerId].finish;
    
    // Calculate average rating of other players
    const otherRatings = playerIds
      .filter(id => id !== playerId)
      .map(id => currentRatings[id]);
    const avgOthersRating = otherRatings.reduce((sum, r) => sum + r, 0) / otherRatings.length;
    
    // Calculate expected and actual scores
    const expectedScore = 1 / (1 + Math.pow(10, (avgOthersRating - playerRating) / 400));
    const actualScore = (numPlayers - finish) / (numPlayers - 1);
    
    // Calculate change
    const change = Math.round(32 * (actualScore - expectedScore));
    
    changes[playerId] = {
      currentRating: playerRating,
      finish,
      expectedScore,
      actualScore,
      change,
      newRating: playerRating + change
    };
  }
  
  return changes;
}

/**
 * Subscribe to real-time Elo rating updates (from history)
 * @param {Function} callback - Function to call when Elo ratings update
 * @returns {Function} Unsubscribe function
 */
export function subscribeToEloRatings(callback) {
  try {
    const historyRef = ref(database, 'ELO_history');
    
    const handleUpdate = (snapshot) => {
      console.log('📊 Real-time ELO_history update received');
      
      const allRatings = {};
      
      if (snapshot.exists()) {
        const historyData = snapshot.val();
        
        // Process each player's history to get current rating
        Object.entries(historyData).forEach(([playerId, playerHistory]) => {
          if (playerHistory && typeof playerHistory === 'object') {
            const entries = Object.values(playerHistory);
            if (entries.length > 0) {
              // Find most recent entry
              const mostRecent = entries.reduce((latest, entry) => {
                return entry.at > latest.at ? entry : latest;
              });
              allRatings[playerId] = mostRecent.value;
            }
          }
        });
      }
      
      console.log('📊 Current ELO ratings from history:', allRatings);
      callback(allRatings);
    };
    
    onValue(historyRef, handleUpdate);
    
    // Return unsubscribe function
    return () => {
      off(historyRef, 'value', handleUpdate);
    };
  } catch (error) {
    console.error('Error subscribing to Elo ratings:', error);
    return () => {}; // Return no-op unsubscribe
  }
}

/**
 * Get all unique players from ACT history and their ELO ratings
 * @param {Array} actHistory - Array of ACT entries
 * @returns {Promise<Object>} Object with player info and ELO ratings
 */
export async function getPlayersWithEloRatings(actHistory = []) {
  try {
    // Get current ELO ratings (both old and new format)
    const rawEloData = await getCurrentEloRatings();
    const normalizedEloRatings = normalizeEloData(rawEloData);
    
    console.log('📊 Raw ELO data:', rawEloData);
    console.log('📊 Normalized ELO ratings:', normalizedEloRatings);
    
    // Extract all unique players from ACT history
    const allPlayers = new Set();
    const playerTeamHistory = {}; // Track which teams/positions players have played
    
    actHistory.forEach(act => {
      if (act.teams) {
        Object.entries(act.teams).forEach(([team, teamData]) => {
          if (teamData.players) {
            Object.entries(teamData.players).forEach(([position, playerName]) => {
              if (playerName && playerName !== `Player ${position}`) {
                allPlayers.add(playerName);
                
                if (!playerTeamHistory[playerName]) {
                  playerTeamHistory[playerName] = [];
                }
                playerTeamHistory[playerName].push({ team, position, date: act.date });
              }
            });
          }
        });
      }
    });
    
    console.log('👥 Found players in history:', Array.from(allPlayers));
    console.log('📋 Player team history:', playerTeamHistory);
    
    // Create player objects with ELO ratings
    const playersWithElo = {};
    
    // Add players from ACT history
    Array.from(allPlayers).forEach(playerName => {
      playersWithElo[playerName] = {
        name: playerName,
        elo: null,
        source: 'history',
        teamHistory: playerTeamHistory[playerName] || []
      };
    });
    
    // Match ELO ratings to players
    Object.entries(normalizedEloRatings).forEach(([eloKey, rating]) => {
      // Try to match ELO key to player name
      let matchedPlayer = null;
      
      // Direct name match
      if (playersWithElo[eloKey]) {
        matchedPlayer = eloKey;
      } else {
        // Try to find partial matches
        const possibleMatches = Object.keys(playersWithElo).filter(playerName =>
          playerName.toLowerCase().includes(eloKey.toLowerCase()) ||
          eloKey.toLowerCase().includes(playerName.toLowerCase())
        );
        
        if (possibleMatches.length === 1) {
          matchedPlayer = possibleMatches[0];
        }
      }
      
      if (matchedPlayer) {
        playersWithElo[matchedPlayer].elo = rating;
        playersWithElo[matchedPlayer].eloKey = eloKey;
      } else {
        // ELO rating exists but no matching player in history
        playersWithElo[eloKey] = {
          name: eloKey,
          elo: rating,
          source: 'elo_only',
          teamHistory: [],
          eloKey: eloKey
        };
      }
    });
    
    console.log('🎯 Final players with ELO:', playersWithElo);
    return playersWithElo;
    
  } catch (error) {
    console.error('Error getting players with ELO ratings:', error);
    return {};
  }
}

/**
 * Get current ELO ratings (raw format)
 */
async function getCurrentEloRatings() {
  try {
    const eloRef = ref(database, 'ELO');
    const snapshot = await get(eloRef);
    return snapshot.exists() ? snapshot.val() : {};
  } catch (error) {
    console.error('Error fetching raw ELO data:', error);
    return {};
  }
}

/**
 * Migrate old ELO data format to new format
 * Converts nested objects to simple key-value pairs
 * @returns {Promise<Object>} Migration results
 */
export async function migrateEloDataFormat() {
  try {
    console.log('🔄 Starting ELO data format migration...');
    
    // Get current data
    const eloRef = ref(database, 'ELO');
    const snapshot = await get(eloRef);
    
    if (!snapshot.exists()) {
      console.log('❌ No ELO data found to migrate');
      return { success: false, message: 'No data to migrate' };
    }
    
    const rawData = snapshot.val();
    console.log('📊 Current ELO data:', rawData);
    
    // Check if migration is needed
    const needsMigration = Object.values(rawData).some(value => 
      typeof value === 'object' && value !== null && 'ELO' in value
    );
    
    if (!needsMigration) {
      console.log('✅ Data is already in correct format');
      return { success: true, message: 'Data already migrated' };
    }
    
    // Normalize the data
    const normalizedData = normalizeEloData(rawData);
    console.log('📊 Normalized data:', normalizedData);
    
    if (Object.keys(normalizedData).length === 0) {
      console.log('❌ No valid data found after normalization');
      return { success: false, message: 'No valid data after normalization' };
    }
    
    // Clear old data and write new format
    await set(eloRef, normalizedData);
    
    console.log('✅ ELO data migration completed successfully');
    return { 
      success: true, 
      message: `Migrated ${Object.keys(normalizedData).length} ELO ratings`,
      data: normalizedData
    };
    
  } catch (error) {
    console.error('❌ Error migrating ELO data:', error);
    throw error;
  }
}

/**
 * Get player status (active/retired)
 * @param {string} playerId - Player identifier
 * @returns {Promise<string>} Player status ('active' or 'retired')
 */
export async function getPlayerStatus(playerId) {
  try {
    const statusRef = ref(database, `playerStatus/${playerId}`);
    const snapshot = await get(statusRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    // Default to active if no status is set
    return 'active';
  } catch (error) {
    console.error(`Error fetching status for player ${playerId}:`, error);
    return 'active'; // Default to active on error
  }
}

/**
 * Get all player statuses
 * @returns {Promise<Object>} Object with all player statuses
 */
export async function getAllPlayerStatuses() {
  try {
    const statusRef = ref(database, 'playerStatus');
    const snapshot = await get(statusRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    return {};
  } catch (error) {
    console.error('Error fetching player statuses:', error);
    return {};
  }
}

/**
 * Set player status (active/retired)
 * @param {string} playerId - Player identifier
 * @param {string} status - Player status ('active' or 'retired')
 * @returns {Promise<void>}
 */
export async function setPlayerStatus(playerId, status) {
  try {
    if (status !== 'active' && status !== 'retired') {
      throw new Error('Invalid status. Must be "active" or "retired"');
    }
    
    const statusRef = ref(database, `playerStatus/${playerId}`);
    await set(statusRef, status);
    
    console.log(`✅ Set status for ${playerId} to ${status}`);
  } catch (error) {
    console.error(`Error setting status for player ${playerId}:`, error);
    throw error;
  }
}

/**
 * Subscribe to player status updates
 * @param {Function} callback - Function to call when statuses update
 * @returns {Function} Unsubscribe function
 */
export function subscribeToPlayerStatuses(callback) {
  try {
    const statusRef = ref(database, 'playerStatus');
    
    const handleUpdate = (snapshot) => {
      const statuses = snapshot.exists() ? snapshot.val() : {};
      callback(statuses);
    };
    
    onValue(statusRef, handleUpdate);
    
    // Return unsubscribe function
    return () => {
      off(statusRef, 'value', handleUpdate);
    };
  } catch (error) {
    console.error('Error subscribing to player statuses:', error);
    return () => {}; // Return no-op unsubscribe
  }
}